import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  Grid,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  IconButton
} from '@mui/material';
import {
  Speed as SpeedIcon,
  TrendingUp as TrendingUpIcon,
  CheckCircle as CheckCircleIcon,
  Settings as SettingsIcon,
  Refresh as RefreshIcon,
  Lightbulb as LightbulbIcon
} from '@mui/icons-material';
import { aiService, taskService } from '../services/api';
import { useNavigate } from 'react-router-dom';

const PrioritizationWidget = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [priorities, setPriorities] = useState(null);
  const [showSettings, setShowSettings] = useState(false);
  const [prefs, setPrefs] = useState({
    energy_profile: '',
    work_habits: ''
  });

  useEffect(() => {
    loadPriorities();
  }, []);

  const loadPriorities = async () => {
    setLoading(true);
    try {
      // First try to get priorities without explicit prefs (backend uses saved ones)
      const response = await aiService.getDailyPriorities({});
      setPriorities(response.data.result);
      
      // If we got a result but it seems generic or we want to prompt user occasionally
      // (Logic can be refined)
    } catch (error) {
      console.error('Error loading priorities:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleUpdatePrefs = async () => {
    try {
      await aiService.updatePreferences(prefs);
      setShowSettings(false);
      loadPriorities(); // Reload with new prefs
    } catch (error) {
      console.error('Error updating prefs:', error);
    }
  };

  const handleTaskAction = async (task, action) => {
    try {
      // Record feedback
      await aiService.recordPriorityFeedback({
        selectedTaskId: task.task_id,
        selectedTaskTitle: task.task_title,
        selectedTaskPriority: task.rank, // Using rank as priority indicator in this context
        suggestedTasks: priorities.top_tasks.map(t => t.task_id)
      });

      if (action === 'start') {
        navigate(`/projects/${task.project_id || ''}`); // Navigation logic might need refinement depending on task structure
      } else if (action === 'complete') {
        // Optimistically remove from list or mark done
        // For now just refresh
        await taskService.updateStatus(task.task_id, 'completed');
        loadPriorities();
      }
    } catch (error) {
      console.error('Error handling task action:', error);
    }
  };

  const getEnergyColor = (match) => {
    if (match?.toLowerCase().includes('peu')) return 'success';
    if (match?.toLowerCase().includes('beaucoup')) return 'error';
    return 'primary';
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!priorities || priorities.top_tasks?.length === 0) {
    return (
      <Card elevation={2} sx={{ mb: 4, background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)' }}>
        <CardContent sx={{ textAlign: 'center', py: 4 }}>
          <Typography variant="h6" gutterBottom>
            Tout est calme... 🍃
          </Typography>
          <Typography color="text.secondary">
            Aucune tâche prioritaire pour le moment.
          </Typography>
          <Button startIcon={<RefreshIcon />} onClick={loadPriorities} sx={{ mt: 2 }}>
            Actualiser
          </Button>
        </CardContent>
      </Card>
    );
  }

  return (
    <Box sx={{ mb: 4 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h5" fontWeight="bold" sx={{ display: 'flex', alignItems: 'center' }}>
          <LightbulbIcon sx={{ mr: 1, color: '#fbc02d' }} />
          Focus du jour
        </Typography>
        <Box>
            <IconButton onClick={() => setShowSettings(true)} size="small">
                <SettingsIcon />
            </IconButton>
            <IconButton onClick={loadPriorities} size="small">
                <RefreshIcon />
            </IconButton>
        </Box>
      </Box>

      {priorities.summary && (
        <Typography variant="body1" sx={{ mb: 3, fontStyle: 'italic', color: 'text.secondary' }}>
          "{priorities.summary}"
        </Typography>
      )}

      <Grid container spacing={2}>
        {priorities.top_tasks.map((task, index) => (
          <Grid item xs={12} md={4} key={`${task.task_id}-${index}`}>
            <Card 
                elevation={3} 
                sx={{ 
                    height: '100%', 
                    display: 'flex', 
                    flexDirection: 'column',
                    position: 'relative',
                    borderTop: index === 0 ? '4px solid #f44336' : (index === 1 ? '4px solid #ff9800' : '4px solid #2196f3')
                }}
            >
              <CardContent sx={{ flexGrow: 1 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                    <Chip 
                        label={`#${index + 1}`} 
                        size="small" 
                        color={index === 0 ? 'error' : (index === 1 ? 'warning' : 'primary')} 
                        sx={{ fontWeight: 'bold' }}
                    />
                    {task.energy_match && (
                        <Chip 
                            icon={<SpeedIcon />} 
                            label="Énergie" 
                            size="small" 
                            variant="outlined"
                            color={getEnergyColor(task.energy_match)}
                        />
                    )}
                </Box>
                
                <Typography variant="h6" gutterBottom fontWeight="bold" sx={{ mt: 1 }}>
                  {task.task_title}
                </Typography>
                
                <Typography variant="body2" color="text.secondary" paragraph>
                  {task.reason}
                </Typography>

                {task.energy_match && (
                    <Typography variant="caption" display="block" color="text.secondary" sx={{ mt: 1 }}>
                        💡 {task.energy_match}
                    </Typography>
                )}
              </CardContent>
              
              <Box sx={{ p: 2, pt: 0, display: 'flex', gap: 1 }}>
                <Button 
                    variant="contained" 
                    fullWidth 
                    size="small"
                    startIcon={<TrendingUpIcon />}
                    onClick={() => handleTaskAction(task, 'start')}
                >
                  Commencer
                </Button>
                <Button 
                    variant="outlined" 
                    color="success"
                    size="small"
                    onClick={() => handleTaskAction(task, 'complete')}
                >
                  <CheckCircleIcon />
                </Button>
              </Box>
            </Card>
          </Grid>
        ))}
      </Grid>

      <Dialog open={showSettings} onClose={() => setShowSettings(false)}>
        <DialogTitle>Personnaliser l'IA de Priorisation</DialogTitle>
        <DialogContent>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Aidez l'IA à mieux comprendre votre rythme de travail.
            </Typography>
            <FormControl fullWidth margin="normal">
                <InputLabel>Niveau d'énergie habituel</InputLabel>
                <Select
                    value={prefs.energy_profile}
                    label="Niveau d'énergie habituel"
                    onChange={(e) => setPrefs({...prefs, energy_profile: e.target.value})}
                >
                    <MenuItem value="Matin (Productif avant midi)">Matin (Productif avant midi)</MenuItem>
                    <MenuItem value="Après-midi (Diesel)">Après-midi (Diesel)</MenuItem>
                    <MenuItem value="Soir (Oiseau de nuit)">Soir (Oiseau de nuit)</MenuItem>
                    <MenuItem value="Variable">Variable</MenuItem>
                </Select>
            </FormControl>
            <TextField
                fullWidth
                margin="normal"
                label="Habitudes de travail spécifiques"
                multiline
                rows={3}
                value={prefs.work_habits}
                onChange={(e) => setPrefs({...prefs, work_habits: e.target.value})}
                placeholder="Ex: Pas de réunions le matin, je préfère les petites tâches après le déjeuner..."
            />
        </DialogContent>
        <DialogActions>
            <Button onClick={() => setShowSettings(false)}>Annuler</Button>
            <Button onClick={handleUpdatePrefs} variant="contained">Enregistrer</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default PrioritizationWidget;
